// BillBreakdown.jsx
import React, { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import axios from "./api"; // adjust your axios import

const BillBreakdown = ({
  open,
  onClose,
  cart = [],
  discountAmount = 0,
  additionalCharges = [],
  width = 900,
}) => {
  const [loading, setLoading] = useState(false);
  const [taxConfig, setTaxConfig] = useState(null);

  // ─────────────────────────────── fetch GST + Vendor state
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const vendorId = localStorage.getItem("vendorId");
        if (!vendorId) return;

        // fetch vendor
        const vendorRes = await axios.get(`/getVendor/${vendorId}`);
        const vendor = vendorRes.data;

        // fetch GST config (latest one)
        const gstRes = await axios.get(`/gst`);
        const gstConfigs = gstRes.data;
        const gst = gstConfigs?.[0]; // latest (already sorted desc in API)

        let taxDetails;
        if (vendor.state === gst.state) {
          taxDetails = {
            type: "CGST_SGST",
            cgst: gst.cgst,
            sgst: gst.sgst,
          };
        } else {
          taxDetails = {
            type: "IGST",
            igst: gst.igst,
          };
        }
        setTaxConfig(taxDetails);
      } catch (err) {
        console.error("Error fetching GST/Vendor:", err);
      } finally {
        setLoading(false);
      }
    };

    if (open) fetchData();
  }, [open]);

  // ─────────────────────────────── totals
  const totals = cart.reduce(
    (acc, item) => {
      const price = +item.price || 0;
      const qty = item.quantity || 0;

      let taxRate = 0;
      if (taxConfig?.type === "CGST_SGST") {
        taxRate = (taxConfig.cgst + taxConfig.sgst) / 100;
      } else if (taxConfig?.type === "IGST") {
        taxRate = taxConfig.igst / 100;
      }

      const rateExc = price / (1 + taxRate);
      const gst = rateExc * taxRate;

      acc.beforeTax += rateExc * qty;
      acc.gst += gst * qty;
      return acc;
    },
    { beforeTax: 0, gst: 0 }
  );

  // Normalize additional charges
  const normalizedCharges = (additionalCharges || []).map((c, idx) => ({
    key: c._id || idx,
    label: c.label || c.name || c.chargeName || `Charge ${idx + 1}`,
    amount: parseFloat(c.amount ?? c.price ?? c.chargeAmount ?? 0),
  }));

  const totalAdditional = normalizedCharges.reduce(
    (sum, c) => sum + c.amount,
    0
  );

  const grandTotal =
    totals.beforeTax + totals.gst + totalAdditional - discountAmount;

  // ─────────────────────────────── styles
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
    marginTop: "10px",
  };
  const thTdStyle = {
    border: "1px solid #ccc",
    padding: "8px",
    textAlign: "center",
  };
  const boldRight = {
    ...thTdStyle,
    fontWeight: "bold",
    textAlign: "right",
  };

  // ─────────────────────────────── render
  return (
    <Modal
      title="Bill Breakdown"
      open={open}
      onCancel={onClose}
      footer={null}
      width={width}
    >
      {loading || !taxConfig ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thTdStyle}>Sr No</th>
              <th style={thTdStyle}>Item</th>
              <th style={thTdStyle}>Qty</th>
              <th style={thTdStyle}>Rate (Inc. Tax)</th>
              <th style={thTdStyle}>Rate (Exc. Tax)</th>
              <th style={thTdStyle}>Amount</th>
            </tr>
          </thead>

          <tbody>
            {cart.map((item, i) => {
              const price = +item.price || 0;
              const qty = item.quantity || 0;

              let taxRate = 0;
              if (taxConfig?.type === "CGST_SGST") {
                taxRate = (taxConfig.cgst + taxConfig.sgst) / 100;
              } else if (taxConfig?.type === "IGST") {
                taxRate = taxConfig.igst / 100;
              }

              const rateExc = +(price / (1 + taxRate)).toFixed(2);
              const amount = +(rateExc * qty).toFixed(2);

              return (
                <tr key={item._id || i}>
                  <td style={thTdStyle}>{i + 1}</td>
                  <td style={thTdStyle}>{item.name}</td>
                  <td style={thTdStyle}>{qty}</td>
                  <td style={thTdStyle}>₹{price.toFixed(2)}</td>
                  <td style={thTdStyle}>₹{rateExc.toFixed(2)}</td>
                  <td style={thTdStyle}>₹{amount.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
  <tr>
    <td colSpan="5" style={boldRight}>
      Total Before Tax:
    </td>
    <td style={{ ...thTdStyle, textAlign: "right" }}>
      ₹{totals.beforeTax.toFixed(2)}
    </td>
  </tr>

  {taxConfig?.type === "CGST_SGST" && (
    <>
      <tr>
        <td colSpan="5" style={boldRight}>
          CGST ({taxConfig.cgst}%):
        </td>
        <td style={{ ...thTdStyle, textAlign: "right" }}>
          ₹{(totals.beforeTax * (taxConfig.cgst / 100)).toFixed(2)}
        </td>
      </tr>
      <tr>
        <td colSpan="5" style={boldRight}>
          SGST ({taxConfig.sgst}%):
        </td>
        <td style={{ ...thTdStyle, textAlign: "right" }}>
          ₹{(totals.beforeTax * (taxConfig.sgst / 100)).toFixed(2)}
        </td>
      </tr>
    </>
  )}

  {taxConfig?.type === "IGST" && (
    <tr>
      <td colSpan="5" style={boldRight}>
        IGST ({taxConfig.igst}%):
      </td>
      <td style={{ ...thTdStyle, textAlign: "right" }}>
        ₹{totals.gst.toFixed(2)}
      </td>
    </tr>
  )}

  {normalizedCharges.map((c) => (
    <tr key={c.key}>
      <td colSpan="5" style={boldRight}>
        {c.label}:
      </td>
      <td style={{ ...thTdStyle, textAlign: "right" }}>₹{c.amount.toFixed(2)}</td>
    </tr>
  ))}

  {discountAmount > 0 && (
    <tr>
      <td colSpan="5" style={boldRight}>
        Discount:
      </td>
      <td style={{ ...thTdStyle, color: "red", textAlign: "right" }}>
        -₹{discountAmount.toFixed(2)}
      </td>
    </tr>
  )}

  <tr>
    <td colSpan="5" style={{ ...boldRight, fontSize: "16px" }}>
      Grand Total:
    </td>
    <td
      style={{
        ...thTdStyle,
        fontWeight: "bold",
        fontSize: "16px",
        textAlign: "right",
      }}
    >
      ₹{grandTotal.toFixed(2)}
    </td>
  </tr>
</tfoot>

        </table>
      )}
    </Modal>
  );
};

export default BillBreakdown;
